import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import clsx from 'clsx';
import {
  FiAlertTriangle,
  FiCheck,
  FiClock,
  FiBell,
  FiPlus,
  FiEdit2,
  FiTrash2,
} from 'react-icons/fi';
import { statusApi } from '../../services/api';
import { AlertRule, AlertInstance, AlertSeverity, AlertStatus } from '../../types';

const severityColor: Record<AlertSeverity, string> = {
  info: 'bg-blue-100 text-blue-800',
  warning: 'bg-amber-100 text-amber-800',
  error: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

const statusBadge: Record<AlertStatus, { bg: string; text: string; icon: React.ReactNode }> = {
  active: { bg: 'bg-red-100', text: 'text-red-800', icon: <FiAlertTriangle className="w-4 h-4" /> },
  acknowledged: { bg: 'bg-amber-100', text: 'text-amber-800', icon: <FiClock className="w-4 h-4" /> },
  resolved: { bg: 'bg-green-100', text: 'text-green-800', icon: <FiCheck className="w-4 h-4" /> },
};

const AlertManager: React.FC = () => {
  const [activeAlerts, setActiveAlerts] = useState<AlertInstance[]>([]);
  const [alertRules, setAlertRules] = useState<AlertRule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'alerts' | 'rules'>('alerts');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [alerts, rules] = await Promise.all([
        statusApi.getActiveAlerts().catch(() => []),
        statusApi.getAlertRules().catch(() => []),
      ]);
      setActiveAlerts(alerts);
      setAlertRules(rules);
    } catch (error) {
      console.error('Failed to fetch alert data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcknowledge = async (alertId: string) => {
    try {
      await statusApi.acknowledgeAlert(alertId);
      await fetchData();
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
    }
  };

  const handleResolve = async (alertId: string) => {
    try {
      await statusApi.resolveAlert(alertId);
      await fetchData();
    } catch (error) {
      console.error('Failed to resolve alert:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Alert Management</h1>
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl p-6 h-24"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Alert Management</h1>
        <button
          type="button"
          className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
        >
          <FiPlus className="w-4 h-4" />
          Create Rule
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            type="button"
            onClick={() => setActiveTab('alerts')}
            className={clsx(
              'py-4 px-1 border-b-2 font-medium text-sm transition-colors',
              activeTab === 'alerts'
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            )}
          >
            Active Alerts ({activeAlerts.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('rules')}
            className={clsx(
              'py-4 px-1 border-b-2 font-medium text-sm transition-colors',
              activeTab === 'rules'
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            )}
          >
            Alert Rules ({alertRules.length})
          </button>
        </nav>
      </div>

      {/* Active Alerts Tab */}
      {activeTab === 'alerts' && (
        <div className="space-y-4">
          {activeAlerts.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <FiBell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No active alerts</h3>
              <p className="mt-2 text-gray-500">All systems are operating within normal parameters.</p>
            </div>
          ) : (
            activeAlerts.map((alert) => (
              <div
                key={alert.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div
                      className={clsx(
                        'w-10 h-10 rounded-lg flex items-center justify-center',
                        statusBadge[alert.status].bg,
                        statusBadge[alert.status].text
                      )}
                    >
                      {statusBadge[alert.status].icon}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{alert.ruleName}</h3>
                        <span
                          className={clsx(
                            'px-2 py-0.5 rounded text-xs font-medium',
                            severityColor[alert.severity]
                          )}
                        >
                          {alert.severity.toUpperCase()}
                        </span>
                      </div>
                      <p className="mt-1 text-gray-600">{alert.message}</p>
                      <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                        <span>Triggered: {format(new Date(alert.createdAt), 'MMM d, h:mm a')}</span>
                        {alert.currentValue && (
                          <span>Current value: {alert.currentValue}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {alert.status === 'active' && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleAcknowledge(alert.id)}
                          className="px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors"
                        >
                          Acknowledge
                        </button>
                        <button
                          type="button"
                          onClick={() => handleResolve(alert.id)}
                          className="px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                        >
                          Resolve
                        </button>
                      </>
                    )}
                    {alert.status === 'acknowledged' && (
                      <button
                        type="button"
                        onClick={() => handleResolve(alert.id)}
                        className="px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                      >
                        Resolve
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Alert Rules Tab */}
      {activeTab === 'rules' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rule
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Metric
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Condition
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Severity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {alertRules.map((rule) => (
                <tr key={rule.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <p className="font-medium text-gray-900">{rule.name}</p>
                      {rule.description && (
                        <p className="text-sm text-gray-500">{rule.description}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {rule.metricType}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {rule.condition} {rule.threshold}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={clsx(
                        'px-2 py-1 rounded text-xs font-medium',
                        severityColor[rule.severity]
                      )}
                    >
                      {rule.severity}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={clsx(
                        'px-2 py-1 rounded text-xs font-medium',
                        rule.isEnabled
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      )}
                    >
                      {rule.isEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="Edit rule"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Delete rule"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AlertManager;
